import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../utils/supabase';

function LessonModal({ open, onClose, onSave, lesson, position, courseId, categories, refreshCategories, isOnboarding = false, section, prefill }) {
  const isEdit = !!lesson;
  const [lessonPosition, setLessonPosition] = useState(position);
  const [lessonTitle, setLessonTitle] = useState('');
  const [questions, setQuestions] = useState(lesson ? lesson.questions : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showQTypeMenu, setShowQTypeMenu] = useState(false);
  const [qTypeMenuHovered, setQTypeMenuHovered] = useState(false);
  const [qTypeButtonHovered, setQTypeButtonHovered] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const dropdownRef = useRef(null);
  const [pendingCategories, setPendingCategories] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [originalLesson, setOriginalLesson] = useState(null);
  const [lessonDescription, setLessonDescription] = useState('');
  const [activeCategoryQuestionIdx, setActiveCategoryQuestionIdx] = useState(null);
  
  const isAllSections = section === 'all';
  const allowedTypes = useMemo(() => (
    isOnboarding
      ? ['multi_choice', 'information', 'onboard_intro', 'onboard_outro']
      : (isAllSections ? ['multi_choice', 'information', 'reflection'] : (section === 'apply' ? ['reflection'] : (section === 'description' ? [] : ['multi_choice', 'information'])))
  ), [isAllSections, isOnboarding, section]);
  const isLessonTitleOnly = !isOnboarding && !section;
  const isDescriptionOnly = section === 'description';

  const normalizeName = (s = '') => s.trim().toLowerCase();

  const isQuizType = (t) => t === 'multi_choice' || t === 'information';
  const isApplyType = (t) => t === 'reflection';

  // Merge DB categories and locally pending ones for instant availability
  const availableCategories = [
    ...categories,
    ...pendingCategories
      .filter(pc => !categories.some(c => normalizeName(c.category_name) === normalizeName(pc.category_name)))
  ];

  useEffect(() => {
    if (open) {
      setLessonPosition(position);
      setLessonTitle(lesson ? lesson.title : '');
      const initialQuestions = lesson ? (lesson.questions || []) : [];
      const filtered = initialQuestions.filter(q => allowedTypes.includes(q.type));
      if (isOnboarding) {
        const hasIntro = filtered.some(q => q.type === 'onboard_intro');
        const hasOutro = filtered.some(q => q.type === 'onboard_outro');
        const withFixed = [
          ...(hasIntro ? [] : [{ type: 'onboard_intro', title: '', body_text: '', order: null, isNew: !isEdit }]),
          ...filtered,
          ...(hasOutro ? [] : [{ type: 'onboard_outro', title: '', body_text: '', order: null, isNew: !isEdit }]),
        ];
        setQuestions((isLessonTitleOnly || isDescriptionOnly) ? [] : withFixed);
      } else {
        setQuestions((isLessonTitleOnly || isDescriptionOnly) ? [] : filtered);
      }
      setSaving(false);
      setError('');
      setValidationErrors([]);
      setPendingCategories([]);
      setShowAddCategory(false);
      setNewCategoryName('');
      setCategoryError('');
      setActiveCategoryQuestionIdx(null);
      setOriginalLesson(lesson ? JSON.parse(JSON.stringify(lesson)) : null);
      setLessonDescription(lesson?.description || '');
      setShowQTypeMenu(false);
    }
  }, [open, lesson, position, section, isOnboarding, allowedTypes, isDescriptionOnly, isEdit, isLessonTitleOnly]);

  // Prefill draft when creating a new lesson (only applies when lesson is null)
  useEffect(() => {
    if (!open) return;
    if (lesson) return;
    if (!prefill) return;
    setLessonTitle(prefill.title || '');
    setLessonDescription(prefill.description || '');
    const draftQs = Array.isArray(prefill.questions) ? prefill.questions : [];
    const filtered = draftQs.filter(q => allowedTypes.includes(q.type));
    setQuestions(filtered);
    setOriginalLesson(null);
  }, [open, lesson, prefill, section, allowedTypes]);

  // Helper to check if form is dirty
  const isDirty = () => {
    if (!originalLesson) return ((isLessonTitleOnly || isDescriptionOnly) ? false : questions.length > 0) || (!section && lessonTitle.trim() !== '') || (isDescriptionOnly && lessonDescription.trim() !== '');
    if (isLessonTitleOnly) {
      return lessonTitle !== (originalLesson?.title || lesson?.title || '');
    }
    if (isDescriptionOnly) {
      return (lessonDescription || '') !== (originalLesson?.description || lesson?.description || '');
    }
    if (isAllSections) {
      if ((lessonTitle || '') !== (originalLesson?.title || lesson?.title || '')) return true;
      if ((lessonDescription || '') !== (originalLesson?.description || lesson?.description || '')) return true;
    }
    if (!section && !isOnboarding && lessonPosition !== (originalLesson?.position || lesson?.position)) return true;
    if (!section && (lessonTitle !== (originalLesson?.title || lesson?.title || ''))) return true;
    const originalSectionQuestions = (originalLesson?.questions || lesson?.questions || []).filter(q => allowedTypes.includes(q.type));
    if (questions.length !== originalSectionQuestions.length) return true;
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      // Find original question by id within this section to avoid index mismatch after reordering
      const oq = q.id ? originalSectionQuestions.find((orig) => orig.id === q.id) : undefined;
      if (!oq || q.type !== oq.type) return true;
      if (q.order !== oq.order) return true;
      if (q.type === 'multi_choice') {
        if (q.question_text !== oq.question_text) return true;
        if (JSON.stringify(q.options) !== JSON.stringify(oq.options)) return true;
      } else if (q.type === 'reflection') {
        if (q.question_text !== oq.question_text) return true;
        if ((q.extra_details || '') !== (oq.extra_details || '')) return true;
        if ((q.follow_up_question || '') !== (oq.follow_up_question || '')) return true;
        const qCatIds = (q.categories || []).map(c => c.id || normalizeName(c.category_name)).sort();
        const oqCatIds = (oq.categories || []).map(c => c.id || normalizeName(c.category_name)).sort();
        if (JSON.stringify(qCatIds) !== JSON.stringify(oqCatIds)) return true;
      } else if (q.type === 'information') {
        if (q.title !== oq.title) return true;
        if (q.body_text !== oq.body_text) return true;
      } else if (q.type === 'onboard_intro' || q.type === 'onboard_outro') {
        if (q.title !== oq.title) return true;
        if (q.body_text !== oq.body_text) return true;
      }
    }
    return false;
  };

  // Add Question
  const handleAddQuestion = (type) => {
    // Order is per-section for regular lessons:
    // - Quiz: multi_choice + information
    // - Apply: reflection
    const quizCount = (questions || []).filter(q => isQuizType(q.type)).length;
    const applyCount = (questions || []).filter(q => isApplyType(q.type)).length;
    const newOrder = (type === 'reflection') ? (applyCount + 1) : (quizCount + 1);
    if (type === 'multi_choice') {
      setQuestions([
        ...questions,
        {
          type: 'multi_choice',
          question_text: '',
          options: [
            { text: '', is_correct: false },
            { text: '', is_correct: false }
          ],
          order: newOrder,
          isNew: true,
        },
      ]);
    } else if (type === 'reflection') {
      setQuestions([
        ...questions,
        {
          type: 'reflection',
          question_text: '',
          extra_details: '',
          follow_up_question: '',
          categories: [],
          order: newOrder,
          isNew: true,
        },
      ]);
    } else if (type === 'information') {
      setQuestions([
        ...questions,
        {
          type: 'information',
          title: '',
          body_text: '',
          order: newOrder,
          isNew: true,
        },
      ]);
    }
  };

  // Remove Question
  const handleRemoveQuestion = (idx) => {
    const target = questions[idx];
    if (target && (target.type === 'onboard_intro' || target.type === 'onboard_outro')) return;
    const updatedQuestions = questions.filter((_, i) => i !== idx);
    if (isAllSections) {
      // Reorder per section (quiz vs apply)
      let quizCounter = 0;
      let applyCounter = 0;
      const reordered = updatedQuestions.map((q) => {
        if (q.type === 'onboard_intro' || q.type === 'onboard_outro') return { ...q, order: null };
        if (isApplyType(q.type)) return { ...q, order: (++applyCounter) };
        return { ...q, order: (++quizCounter) };
      });
      setQuestions(reordered);
      return;
    }
    // Default behavior: reorder within the current section
    let counter = 0;
    const reorderedQuestions = updatedQuestions.map((q) => ({
      ...q,
      order: (q.type === 'onboard_intro' || q.type === 'onboard_outro') ? null : (++counter)
    }));
    setQuestions(reorderedQuestions);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, idx) => {
    e.dataTransfer.setData('text/plain', idx);
    e.target.style.opacity = '0.5';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e, idx) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#4f46e5';
    e.currentTarget.style.backgroundColor = '#f0f4ff';
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.borderColor = '#eee';
    e.currentTarget.style.backgroundColor = '#fafbfc';
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    const dragIdx = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIdx === dropIdx) return;
    
    // Reset styles
    e.currentTarget.style.borderColor = '#eee';
    e.currentTarget.style.backgroundColor = '#fafbfc';
    
    // Reorder questions
    const newQuestions = [...questions];
    const draggedQuestion = newQuestions[dragIdx];
    
    // Remove from original position
    newQuestions.splice(dragIdx, 1);
    
    // Insert at new position
    newQuestions.splice(dropIdx, 0, draggedQuestion);
    
    if (isAllSections) {
      // Keep quiz/apply order separate.
      let quizCounter = 0;
      let applyCounter = 0;
      const reordered = newQuestions.map((q) => {
        if (q.type === 'onboard_intro' || q.type === 'onboard_outro') return { ...q, order: null };
        if (isApplyType(q.type)) return { ...q, order: (++applyCounter) };
        return { ...q, order: (++quizCounter) };
      });
      setQuestions(reordered);
      return;
    }

    // Default behavior: update order values for normal questions only
    let counter = 0;
    const reorderedQuestions = newQuestions.map((q) => ({
      ...q,
      order: (q.type === 'onboard_intro' || q.type === 'onboard_outro') ? null : (++counter)
    }));
    setQuestions(reorderedQuestions);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  // Update Question
  const handleUpdateQuestion = (idx, updatedQ) => {
    setQuestions(questions.map((q, i) => (i === idx ? updatedQ : q)));
  };

  // Add option to multi-choice question
  const handleAddOption = (questionIdx) => {
    const question = questions[questionIdx];
    if (question.options.length < 4) {
      const updatedQuestion = {
        ...question,
        options: [...question.options, { text: '', is_correct: false }]
      };
      handleUpdateQuestion(questionIdx, updatedQuestion);
    }
  };

  // Remove option from multi-choice question
  const handleRemoveOption = (questionIdx, optionIdx) => {
    const question = questions[questionIdx];
    if (question.options.length > 2) {
      const updatedOptions = question.options.filter((_, i) => i !== optionIdx);
      const updatedQuestion = {
        ...question,
        options: updatedOptions
      };
      handleUpdateQuestion(questionIdx, updatedQuestion);
    }
  };

  // Update option text
  const handleUpdateOptionText = (questionIdx, optionIdx, text) => {
    const question = questions[questionIdx];
    const updatedOptions = [...question.options];
    updatedOptions[optionIdx] = { ...updatedOptions[optionIdx], text };
    const updatedQuestion = { ...question, options: updatedOptions };
    handleUpdateQuestion(questionIdx, updatedQuestion);
  };

  // Update option correctness
  const handleUpdateOptionCorrect = (questionIdx, optionIdx, isCorrect) => {
    const question = questions[questionIdx];
    const updatedOptions = [...question.options];
    updatedOptions[optionIdx] = { ...updatedOptions[optionIdx], is_correct: isCorrect };
    const updatedQuestion = { ...question, options: updatedOptions };
    handleUpdateQuestion(questionIdx, updatedQuestion);
  };

  // Validate all questions
  const validateQuestions = () => {
    if (isLessonTitleOnly) return true;
    const errors = questions.map(q => {
      if (q.type === 'multi_choice') {
        if (!q.question_text || !q.question_text.trim()) return 'Question text is required.';
        if (q.options.some(opt => !opt.text || !opt.text.trim())) return 'All option texts are required.';
        if (!q.options.some(opt => opt.is_correct)) return 'At least one option must be marked as correct.';
      } else if (q.type === 'reflection') {
        if (!q.question_text || !q.question_text.trim()) return 'Prompt text is required.';
        if ((q.follow_up_question || '').length > 1500) return 'Follow-up question must be 1500 characters or fewer.';
      } else if (q.type === 'information' || q.type === 'onboard_intro' || q.type === 'onboard_outro') {
        if (!q.title || !q.title.trim()) return 'Title is required for information questions.';
        if (!q.body_text || !q.body_text.trim()) return 'Body text is required for information questions.';
      }
      return null;
    });
    setValidationErrors(errors);
    return errors.every(e => !e);
  };

  // Save Lesson
  const handleSaveLesson = async () => {
    if (!validateQuestions()) return;
    setSaving(true);
    setError('');
    try {
      if (isDescriptionOnly) {
        let lessonId = lesson ? lesson.lesson_id : null;
        if (!lessonId) { setSaving(false); return; }
        await supabase
          .from('lessons')
          .update({ description: (lessonDescription || '').trim() || null })
          .eq('lesson_id', lessonId);
        setSaving(false);
        onSave && onSave({ ...lesson, lesson_id: lessonId, description: (lessonDescription || '').trim() || null });
        onClose();
        return;
      }
      if (isLessonTitleOnly) {
        let lessonId = lesson ? lesson.lesson_id : null;
        if (!lessonId) {
          setSaving(false);
          return;
        }
        if (lessonTitle !== (originalLesson?.title || lesson?.title || '')) {
          await supabase
            .from('lessons')
            .update({ title: (lessonTitle || '').trim() || null })
            .eq('lesson_id', lessonId);
        }
        setSaving(false);
        onSave && onSave({ ...lesson, lesson_id: lessonId, title: (lessonTitle || '').trim() || null });
        onClose();
        return;
      }
      
      // Prepare working copy and ensure sequential order values.
      // For regular lessons in all-sections mode, order is per section:
      // - Quiz (multi_choice + information): 1..n
      // - Apply (reflection): 1..n
      let quizCounter = 0;
      let applyCounter = 0;
      const updatedQuestions = [...questions].map((q) => {
        if (q.type === 'onboard_intro' || q.type === 'onboard_outro') return { ...q, order: null };
        if (isAllSections && isApplyType(q.type)) return { ...q, order: (++applyCounter) };
        if (isAllSections && isQuizType(q.type)) return { ...q, order: (++quizCounter) };
        // default: single stream per section
        return q;
      });
      if (!isAllSections) {
        let counter = 0;
        for (let i = 0; i < updatedQuestions.length; i++) {
          const q = updatedQuestions[i];
          if (q.type === 'onboard_intro' || q.type === 'onboard_outro') continue;
          updatedQuestions[i] = { ...q, order: (++counter) };
        }
      }

      // Resolve reflection categories in batch: collect all pending names
      const pendingNamesSet = new Set();
      for (const q of updatedQuestions) {
        if (q.type === 'reflection' && Array.isArray(q.categories)) {
          for (const cat of q.categories) {
            if (!cat.id && cat.category_name && normalizeName(cat.category_name)) {
              pendingNamesSet.add(normalizeName(cat.category_name));
            }
          }
        }
      }

      let nameToCategory = {};
      if (pendingNamesSet.size > 0) {
        const pendingNames = Array.from(pendingNamesSet);
        // Find existing categories with same names
        const { data: existingCats } = await supabase
          .from('course_categories')
          .select('id, category_name')
          .eq('course_id', courseId)
          .in('category_name', pendingNames);
        (existingCats || []).forEach(c => { nameToCategory[normalizeName(c.category_name)] = { id: c.id, category_name: c.category_name }; });
        // Create missing ones in bulk
        const missingNames = pendingNames.filter(n => !nameToCategory[n]);
        if (missingNames.length > 0) {
          const payload = missingNames.map(n => ({ course_id: courseId, category_name: n }));
          const { data: inserted } = await supabase
            .from('course_categories')
            .insert(payload)
            .select('id, category_name');
          (inserted || []).forEach(c => { nameToCategory[normalizeName(c.category_name)] = { id: c.id, category_name: c.category_name }; });
        }
      }

      // Replace reflection categories with resolved ids and de-duplicate
      for (let i = 0; i < updatedQuestions.length; i++) {
        const q = updatedQuestions[i];
        if (q.type === 'reflection' && Array.isArray(q.categories)) {
          const resolved = [];
          const seen = new Set();
          for (const cat of q.categories) {
            let item = cat;
            if (!item.id) {
              const lookup = nameToCategory[normalizeName(item.category_name)] || categories.find(c => normalizeName(c.category_name) === normalizeName(item.category_name));
              if (lookup) item = { id: lookup.id, category_name: lookup.category_name };
            }
            const key = item.id ? `id:${item.id}` : `name:${normalizeName(item.category_name)}`;
            if (!seen.has(key)) { seen.add(key); resolved.push(item); }
          }
          updatedQuestions[i] = { ...q, categories: resolved };
        }
      }

      let lessonId = lesson ? lesson.lesson_id : null;
      // Insert lesson first if it doesn't exist
      if (!lessonId) {
        const { data, error: lErr } = await supabase
          .from('lessons')
          .insert({ 
            course_id: courseId, 
            position: isOnboarding ? null : lessonPosition,
            title: (lessonTitle || '').trim() || null,
            lesson_type: isOnboarding ? 'onboarding' : 'regular',
            ...(isAllSections ? { description: (lessonDescription || '').trim() || null } : {})
          })
          .select('lesson_id')
          .single();
        if (lErr) throw lErr;
        lessonId = data.lesson_id;
      }
      
      // Update lesson title if changed (only when not in section editor)
      if ((!section || isAllSections) && (lessonTitle !== (originalLesson?.title || lesson?.title || ''))) {
        await supabase
          .from('lessons')
          .update({ title: (lessonTitle || '').trim() || null })
          .eq('lesson_id', lessonId);
      }
      if (isAllSections && ((lessonDescription || '') !== (originalLesson?.description || lesson?.description || ''))) {
        await supabase
          .from('lessons')
          .update({ description: (lessonDescription || '').trim() || null })
          .eq('lesson_id', lessonId);
      }
      
      // Insert/update questions for this section
      const originalSectionQuestions = (originalLesson?.questions || lesson?.questions || []).filter(q => allowedTypes.includes(q.type));
      for (let i = 0; i < updatedQuestions.length; i++) {
        const q = updatedQuestions[i];
        const oq = q.id ? originalSectionQuestions.find((orig) => orig.id === q.id) : undefined;
        if (!oq || q.type !== oq.type || q.order !== oq.order ||
          (q.type === 'multi_choice' && (q.question_text !== oq.question_text || JSON.stringify(q.options) !== JSON.stringify(oq.options))) ||
          (q.type === 'reflection' && (q.question_text !== oq.question_text || (q.extra_details || '') !== (oq.extra_details || '') || (q.follow_up_question || '') !== (oq.follow_up_question || '') || JSON.stringify((q.categories || []).map(c => c.id).sort()) !== JSON.stringify((oq.categories || []).map(c => c.id).sort()))) ||
          ((q.type === 'information' || q.type === 'onboard_intro' || q.type === 'onboard_outro') && (q.title !== oq.title || q.body_text !== oq.body_text))) {
          if (q.type === 'multi_choice') {
            let questionId = q.id;
            if (!questionId) {
              const { data: qData, error: qErr } = await supabase
                .from('questions')
                .insert({ lesson_id: lessonId, question_type: 'multi_choice', order: q.order })
                .select('id')
                .single();
              if (qErr) throw qErr;
              questionId = qData.id;
              updatedQuestions[i] = { ...q, id: questionId };
              const { error: mcInsertErr } = await supabase
                .from('multi_choice_questions')
                .insert({ question_id: questionId, question_text: q.question_text || '' });
              if (mcInsertErr) throw mcInsertErr;
              for (const option of q.options) {
                await supabase
                  .from('multi_choice_options')
                  .insert({ question_id: questionId, option_text: option.text || '', is_correct: option.is_correct });
              }
            } else {
              if (q.order !== oq.order) {
                await supabase.from('questions').update({ order: q.order }).eq('id', questionId);
              }
              const { data: mcRow, error: mcFetchErr } = await supabase
                .from('multi_choice_questions')
                .select('question_id')
                .eq('question_id', questionId)
                .maybeSingle();
              if (mcFetchErr) throw mcFetchErr;
              if (!mcRow) {
                const { error: mcCreateErr } = await supabase
                  .from('multi_choice_questions')
                  .insert({ question_id: questionId, question_text: q.question_text || '' });
                if (mcCreateErr) throw mcCreateErr;
              } else {
                const { error: mcUpdateErr } = await supabase
                  .from('multi_choice_questions')
                  .update({ question_text: q.question_text || '' })
                  .eq('question_id', questionId);
                if (mcUpdateErr) throw mcUpdateErr;
              }
              await supabase.from('multi_choice_options').delete().eq('question_id', questionId);
              for (const option of q.options) {
                await supabase
                  .from('multi_choice_options')
                  .insert({ question_id: questionId, option_text: option.text || '', is_correct: option.is_correct });
              }
            }
          } else if (q.type === 'reflection') {
            let questionId = q.id;
            if (!questionId) {
              const { data: qData, error: qErr } = await supabase
                .from('questions')
                .insert({ lesson_id: lessonId, question_type: 'reflection', order: q.order })
                .select('id')
                .single();
              if (qErr) throw qErr;
              questionId = qData.id;
              updatedQuestions[i] = { ...q, id: questionId };
              const { error: reflErr } = await supabase
                .from('reflection_questions')
                .insert({
                  question_id: questionId,
                  prompt_text: q.question_text || '',
                  extra_details: q.extra_details || null,
                  follow_up_question: (q.follow_up_question || '').trim() || null,
                });
              if (reflErr) throw reflErr;
            } else {
              if (q.order !== oq.order) {
                await supabase.from('questions').update({ order: q.order }).eq('id', questionId);
              }
              await supabase
                .from('reflection_questions')
                .update({
                  prompt_text: q.question_text || '',
                  extra_details: q.extra_details || null,
                  follow_up_question: (q.follow_up_question || '').trim() || null,
                })
                .eq('question_id', questionId);
            }
            // categories mapping already resolved; rewrite join table
            await supabase.from('reflection_question_categories').delete().eq('reflection_question_id', questionId);
            for (const cat of (q.categories || [])) {
              if (cat.id) {
                await supabase
                  .from('reflection_question_categories')
                  .insert({ reflection_question_id: questionId, category_id: cat.id });
              }
            }
          } else if (q.type === 'information' || q.type === 'onboard_intro' || q.type === 'onboard_outro') {
            let questionId = q.id;
            if (!questionId) {
              const { data: qData, error: qErr } = await supabase
                .from('questions')
                .insert({ lesson_id: lessonId, question_type: (q.type === 'information' ? 'information' : (q.type === 'onboard_intro' ? 'onboard_intro' : 'onboard_outro')), order: q.order })
                .select('id')
                .single();
              if (qErr) throw qErr;
              questionId = qData.id;
              updatedQuestions[i] = { ...q, id: questionId };
              const { error: infoErr } = await supabase
                .from('information_questions')
                .insert({ question_id: questionId, title: q.title || '', body_text: q.body_text || '' });
              if (infoErr) throw infoErr;
            } else {
              if (q.order !== oq.order) {
                await supabase.from('questions').update({ order: q.order }).eq('id', questionId);
              }
              await supabase
                .from('information_questions')
                .update({ title: q.title || '', body_text: q.body_text || '' })
                .eq('question_id', questionId);
            }
          }
        }
      }
      
      // Delete removed questions in this section
      const currentIds = updatedQuestions.filter(q => q.id).map(q => q.id);
      const originalSectionQuestions2 = (originalLesson?.questions || lesson?.questions || []).filter(q => allowedTypes.includes(q.type));
      const originalIds = originalSectionQuestions2.filter(q => q.id).map(q => q.id);
      const deletedIds = originalIds.filter(id => !currentIds.includes(id));
      for (const questionId of deletedIds) {
        await supabase.from('questions').delete().eq('id', questionId);
      }

      // Ensure sequential order stored (normals only).
      if (isAllSections) {
        const quizNormals = updatedQuestions
          .filter(q => q.id && isQuizType(q.type))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((q, idx) => ({ ...q, order: idx + 1 }));
        const applyNormals = updatedQuestions
          .filter(q => q.id && isApplyType(q.type))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((q, idx) => ({ ...q, order: idx + 1 }));
        for (const q of [...quizNormals, ...applyNormals]) {
          await supabase.from('questions').update({ order: q.order }).eq('id', q.id);
        }
      } else {
        const normals = updatedQuestions.filter(q => q.id && q.type !== 'onboard_intro' && q.type !== 'onboard_outro')
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((q, idx) => ({ ...q, order: idx + 1 }));
        for (const q of normals) {
          await supabase.from('questions').update({ order: q.order }).eq('id', q.id);
        }
      }

      setSaving(false);
      // Build updated lesson payload for optimistic update in parent
      const existingOtherQuestions = (originalLesson?.questions || lesson?.questions || []).filter(q => !allowedTypes.includes(q.type));
      const mergedQuestions = [...existingOtherQuestions, ...updatedQuestions];
      const updatedLessonPayload = {
        ...(lesson || {}),
        lesson_id: lesson ? lesson.lesson_id : lessonId,
        position: lesson?.position ?? lessonPosition,
        // In all-sections mode we edit the title inline, so return it immediately for optimistic UI.
        title: ((isAllSections || !section) ? ((lessonTitle || '').trim() || null) : (lesson?.title ?? null)) || lesson?.title || null,
        lesson_type: (lesson ? lesson.lesson_type : (isOnboarding ? 'onboarding' : 'regular')),
        description: (isAllSections ? ((lessonDescription || '').trim() || null) : (lesson?.description ?? null)),
        questions: mergedQuestions
      };
      if (refreshCategories) {
        await refreshCategories();
      }
      onSave && onSave(updatedLessonPayload);
      onClose();
    } catch (err) {
      console.error('Error saving lesson:', err);
      setSaving(false);
      setError(`Failed to save lesson: ${err.message}`);
    }
  };

  useEffect(() => {
    if (showQTypeMenu) {
      const handleClick = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setShowQTypeMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showQTypeMenu]);

  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 10, padding: 32, minWidth: 400, maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 2px 16px #0003', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>&times;</button>
        <h2 style={{ marginTop: 0 }}>{isOnboarding ? 'Onboarding' : `Lesson ${lessonPosition}`}</h2>
        
        {/* Lesson Title: when not editing a specific section OR in all-sections mode */}
        {(!section || isAllSections) && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{isOnboarding ? 'Onboarding Title:' : 'Lesson Title:'}</label>
            <input
              type="text"
              value={lessonTitle}
              onChange={e => setLessonTitle(e.target.value)}
              placeholder={isOnboarding ? "Enter onboarding title (optional)" : "Enter lesson title (optional)"}
              style={{ 
                width: '100%', 
                padding: 8, 
                borderRadius: 4, 
                border: '1px solid #ccc',
                fontSize: 14
              }}
            />
          </div>
        )}

        {/* Description editor */}
        {(isDescriptionOnly || isAllSections) && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Description:</label>
            <textarea
              value={lessonDescription}
              onChange={e => setLessonDescription(e.target.value)}
              placeholder="Enter a lesson description..."
              style={{ width: '100%', minHeight: 120, padding: 8, borderRadius: 4, border: '1px solid #ccc', fontSize: 14, resize: 'vertical' }}
            />
          </div>
        )}
        
        {/* Position is managed by lesson drag-and-drop; no manual input */}
        
        {/* Questions List (scoped to section). Hidden for title-only editing */}
        {!isLessonTitleOnly && (
        <div style={{ marginBottom: 16 }}>
          {/* All-sections mode: split into Quiz vs Apply sections */}
          {isAllSections && (
            <>
              <h3 style={{ margin: '8px 0 10px 0' }}>Quiz</h3>
              {(questions || [])
                .filter(q => isQuizType(q.type))
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((q, idx) => {
                  const masterIdx = questions.indexOf(q);
                  return (
                    <div
                      key={`quiz-${masterIdx}`}
                      draggable={false}
                      style={{
                        border: '1px solid #eee',
                        borderRadius: 6,
                        marginBottom: 10,
                        padding: 10,
                        background: '#fafbfc',
                        position: 'relative',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {`Quiz Q${q.order || (idx + 1)} (${q.type.replace('_', ' ')})`}
                      </div>
                      <button
                        style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer' }}
                        onClick={() => handleRemoveQuestion(masterIdx)}
                      >
                        Delete
                      </button>
                      {q.type === 'multi_choice' ? (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <label>Question Text:</label>
                            <input
                              type="text"
                              value={q.question_text}
                              onChange={e => handleUpdateQuestion(masterIdx, { ...q, question_text: e.target.value })}
                              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                            />
                          </div>
                          <div style={{ marginBottom: 8 }}>
                             <label>Options:</label>
                             {q.options.map((opt, optIdx) => (
                               <div key={optIdx} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                 <input
                                   type="text"
                                   value={opt.text}
                                   onChange={e => {
                                     const updatedOptions = [...q.options];
                                     updatedOptions[optIdx] = { ...updatedOptions[optIdx], text: e.target.value };
                                     handleUpdateQuestion(masterIdx, { ...q, options: updatedOptions });
                                   }}
                                   style={{ width: 'calc(100% - 100px)', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginRight: 8 }}
                                 />
                                 <label style={{ display: 'flex', alignItems: 'center', marginRight: 8, fontSize: 14 }}>
                                   <input
                                     type="checkbox"
                                     checked={opt.is_correct}
                                     onChange={e => {
                                       const updatedOptions = [...q.options];
                                       updatedOptions[optIdx] = { ...updatedOptions[optIdx], is_correct: e.target.checked };
                                       handleUpdateQuestion(masterIdx, { ...q, options: updatedOptions });
                                     }}
                                     style={{ marginRight: 4 }}
                                   />
                                   Correct
                                 </label>
                                 {q.options.length > 2 && (
                                   <button
                                     style={{ background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer', fontSize: 16, fontWeight: 'bold' }}
                                     onClick={() => {
                                       const updatedOptions = q.options.filter((_, i) => i !== optIdx);
                                       handleUpdateQuestion(masterIdx, { ...q, options: updatedOptions });
                                     }}
                                   >
                                     ×
                                   </button>
                                 )}
                               </div>
                             ))}
                             {q.options.length < 4 && (
                               <button
                                 style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 14, cursor: 'pointer' }}
                                 onClick={() => {
                                   const updatedQuestion = { ...q, options: [...q.options, { text: '', is_correct: false }] };
                                   handleUpdateQuestion(masterIdx, updatedQuestion);
                                 }}
                               >
                                 + Add Option
                               </button>
                             )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <label>Title:</label>
                            <input
                              type="text"
                              value={q.title}
                              onChange={e => handleUpdateQuestion(masterIdx, { ...q, title: e.target.value })}
                              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                            />
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <label>Body Text:</label>
                            <textarea
                              value={q.body_text}
                              onChange={e => handleUpdateQuestion(masterIdx, { ...q, body_text: e.target.value })}
                              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4, minHeight: 100, resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
                              placeholder="Enter the information content..."
                            />
                          </div>
                        </>
                      )}
                      {validationErrors[masterIdx] && (
                        <div style={{ color: '#e11d48', marginTop: 4 }}>{validationErrors[masterIdx]}</div>
                      )}
                    </div>
                  );
                })}

              <div style={{ marginBottom: 18 }}>
                <button
                  style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                  onClick={() => setShowQTypeMenu(true)}
                >
                  + Add Quiz Question
                </button>
                {/* Simple inline choices for all-sections mode */}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="modern-button modern-button-secondary" type="button" onClick={() => handleAddQuestion('multi_choice')}>Multi-Choice</button>
                  <button className="modern-button modern-button-secondary" type="button" onClick={() => handleAddQuestion('information')}>Information</button>
                </div>
              </div>

              <h3 style={{ margin: '8px 0 10px 0' }}>Apply it on the Job</h3>
              {(questions || [])
                .filter(q => isApplyType(q.type))
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((q, idx) => {
                  const masterIdx = questions.indexOf(q);
                  return (
                    <div
                      key={`apply-${masterIdx}`}
                      style={{
                        border: '1px solid #eee',
                        borderRadius: 6,
                        marginBottom: 10,
                        padding: 10,
                        background: '#fafbfc',
                        position: 'relative',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {`Apply Q${q.order || (idx + 1)} (reflection)`}
                      </div>
                      <button
                        style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer' }}
                        onClick={() => handleRemoveQuestion(masterIdx)}
                      >
                        Delete
                      </button>
                      <div style={{ marginBottom: 8 }}>
                        <label>Prompt Text:</label>
                        <input
                          type="text"
                          value={q.question_text}
                          onChange={e => handleUpdateQuestion(masterIdx, { ...q, question_text: e.target.value })}
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                        />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label>Extra Details:</label>
                        <textarea
                          value={q.extra_details || ''}
                          onChange={e => handleUpdateQuestion(masterIdx, { ...q, extra_details: e.target.value })}
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4, minHeight: 80, resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
                          placeholder="Add extra guidance on how this should be answered..."
                        />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label>Choices (optional):</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                          {q.categories.map(cat => (
                            <span key={cat.id || cat.category_name} style={{ background: '#e0e7ff', color: '#333', borderRadius: 6, padding: '4px 12px', fontSize: 14, display: 'flex', alignItems: 'center' }}>
                              {cat.category_name}
                              <button style={{ marginLeft: 6, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleUpdateQuestion(masterIdx, { ...q, categories: q.categories.filter(c => {
                                const cName = normalizeName(c.category_name);
                                const targetName = normalizeName(cat.category_name);
                                return cName !== targetName;
                              }) })}>×</button>
                            </span>
                          ))}
                          <button style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 14, cursor: 'pointer' }} onClick={() => { setActiveCategoryQuestionIdx(masterIdx); setShowAddCategory(true); }}>Create New Choice</button>
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          {availableCategories
                            .filter(cat => !q.categories.some(sel => sel.id ? (cat.id === sel.id) : (normalizeName(cat.category_name) === normalizeName(sel.category_name))))
                            .map(cat => (
                            <span
                              key={cat.id || cat.category_name}
                              style={{
                                background: '#f3f4f6',
                                color: '#333',
                                borderRadius: 999,
                                padding: '2px 8px',
                                fontSize: 12,
                                lineHeight: '16px',
                                cursor: 'pointer',
                                flex: '0 0 auto',
                                border: '1px solid #e5e7eb',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={() => handleUpdateQuestion(masterIdx, { ...q, categories: [...q.categories, cat] })}
                            >
                              {cat.category_name}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label>Fallback Follow-Up Question (optional):</label>
                        <input
                          type="text"
                          value={q.follow_up_question || ''}
                          onChange={e => handleUpdateQuestion(masterIdx, { ...q, follow_up_question: e.target.value })}
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                          placeholder="Hardcoded follow-up if AI generation is unavailable..."
                        />
                      </div>
                      {validationErrors[masterIdx] && (
                        <div style={{ color: '#e11d48', marginTop: 4 }}>{validationErrors[masterIdx]}</div>
                      )}
                    </div>
                  );
                })}

              <div style={{ marginBottom: 8 }}>
                <button
                  style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                  onClick={() => handleAddQuestion('reflection')}
                >
                  + Add Apply Question
                </button>
              </div>
            </>
          )}

          {!isAllSections && questions.map((q, idx) => (
             <div 
               key={idx} 
               draggable={!(q.type === 'onboard_intro' || q.type === 'onboard_outro')}
               onDragStart={(e) => handleDragStart(e, idx)}
               onDragOver={handleDragOver}
               onDragEnter={handleDragEnter}
               onDragLeave={handleDragLeave}
               onDrop={(e) => handleDrop(e, idx)}
               onDragEnd={handleDragEnd}
               style={{ 
                 border: '1px solid #eee', 
                 borderRadius: 6, 
                 marginBottom: 10, 
                 padding: 10, 
                 background: '#fafbfc', 
                 position: 'relative',
                 cursor: 'grab',
                 transition: 'all 0.2s ease'
               }}
             >
               <div style={{ fontWeight: 'bold', marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                 <span style={{ marginRight: 8, fontSize: '18px', color: '#666' }}>⋮⋮</span>
                 {q.type === 'onboard_intro' ? 'Onboarding Intro Slide' : q.type === 'onboard_outro' ? 'Onboarding Outro Slide' : `Q${q.order || idx + 1} (${q.type.replace('_', ' ')})`}
               </div>
               {(q.type !== 'onboard_intro' && q.type !== 'onboard_outro') && (
                 <button style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer' }} onClick={() => handleRemoveQuestion(idx)}>
                   Delete
                 </button>
               )}
              {q.type === 'multi_choice' ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <label>Question Text:</label>
                    <input
                      type="text"
                      value={q.question_text}
                      onChange={e => handleUpdateQuestion(idx, { ...q, question_text: e.target.value })}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                     <label>Options:</label>
                     {q.options.map((opt, optIdx) => (
                       <div key={optIdx} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                         <input
                           type="text"
                           value={opt.text}
                           onChange={e => handleUpdateOptionText(idx, optIdx, e.target.value)}
                           style={{ width: 'calc(100% - 100px)', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginRight: 8 }}
                         />
                         <label style={{ display: 'flex', alignItems: 'center', marginRight: 8, fontSize: 14 }}>
                           <input
                             type="checkbox"
                             checked={opt.is_correct}
                             onChange={e => handleUpdateOptionCorrect(idx, optIdx, e.target.checked)}
                             style={{ marginRight: 4 }}
                           />
                           Correct
                         </label>
                         {q.options.length > 2 && (
                           <button 
                             style={{ background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer', fontSize: 16, fontWeight: 'bold' }} 
                             onClick={() => handleRemoveOption(idx, optIdx)}
                           >
                             ×
                           </button>
                         )}
                       </div>
                     ))}
                     {q.options.length < 4 && (
                       <button 
                         style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 14, cursor: 'pointer' }} 
                         onClick={() => handleAddOption(idx)}
                       >
                         + Add Option
                       </button>
                     )}
                  </div>
                </>
              ) : q.type === 'reflection' ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <label>Prompt Text:</label>
                    <input
                      type="text"
                      value={q.question_text}
                      onChange={e => handleUpdateQuestion(idx, { ...q, question_text: e.target.value })}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label>Extra Details:</label>
                    <textarea
                      value={q.extra_details || ''}
                      onChange={e => handleUpdateQuestion(idx, { ...q, extra_details: e.target.value })}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4, minHeight: 80, resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
                      placeholder="Add extra guidance on how this should be answered..."
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label>Choices (optional):</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                      {q.categories.map(cat => (
                        <span key={cat.id || cat.category_name} style={{ background: '#e0e7ff', color: '#333', borderRadius: 6, padding: '4px 12px', fontSize: 14, display: 'flex', alignItems: 'center' }}>
                          {cat.category_name}
                          <button style={{ marginLeft: 6, background: 'none', border: 'none', color: '#e11d48', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleUpdateQuestion(idx, { ...q, categories: q.categories.filter(c => {
                            if (c.id && cat.id) return c.id !== cat.id;
                            const cName = normalizeName(c.category_name);
                            const targetName = normalizeName(cat.category_name);
                            return cName !== targetName;
                          }) })}>×</button>
                        </span>
                      ))}
                      <button style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 14, cursor: 'pointer' }} onClick={() => { setActiveCategoryQuestionIdx(idx); setShowAddCategory(true); }}>Create New Choice</button>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {availableCategories
                        .filter(cat => !q.categories.some(sel => sel.id ? (cat.id === sel.id) : (normalizeName(cat.category_name) === normalizeName(sel.category_name))))
                        .map(cat => (
                        <span
                          key={cat.id || cat.category_name}
                          style={{
                            background: '#f3f4f6',
                            color: '#333',
                            borderRadius: 999,
                            padding: '2px 8px',
                            fontSize: 12,
                            lineHeight: '16px',
                            cursor: 'pointer',
                            flex: '0 0 auto',
                            border: '1px solid #e5e7eb',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => handleUpdateQuestion(idx, { ...q, categories: [...q.categories, cat] })}
                        >
                          {cat.category_name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label>Fallback Follow-Up Question (optional):</label>
                    <input
                      type="text"
                      value={q.follow_up_question || ''}
                      onChange={e => handleUpdateQuestion(idx, { ...q, follow_up_question: e.target.value })}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                      placeholder="Hardcoded follow-up if AI generation is unavailable..."
                    />
                  </div>
                </>
              ) : (q.type === 'information' || q.type === 'onboard_intro' || q.type === 'onboard_outro') ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <label>Title:</label>
                    <input
                      type="text"
                      value={q.title}
                      onChange={e => handleUpdateQuestion(idx, { ...q, title: e.target.value })}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginTop: 4 }}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label>Body Text:</label>
                    <textarea
                      value={q.body_text}
                      onChange={e => handleUpdateQuestion(idx, { ...q, body_text: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: 8, 
                        borderRadius: 4, 
                        border: '1px solid #ccc', 
                        marginTop: 4,
                        minHeight: 100,
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        fontSize: 14
                      }}
                      placeholder="Enter the information content..."
                    />
                  </div>
                </>
              ) : null}
              {validationErrors[idx] && (
                <div style={{ color: '#e11d48', marginTop: 4 }}>{validationErrors[idx]}</div>
              )}
            </div>
          ))}
        </div>
        )}
         {/* Add Question Button and Dropdown (scoped to section). Hidden for title-only and description-only editing */}
        {!isAllSections && !isLessonTitleOnly && !isDescriptionOnly && (
        <div style={{ marginBottom: 24, position: 'relative' }}>
          <button
            onMouseEnter={!isOnboarding && section === 'apply' ? undefined : () => { setShowQTypeMenu(true); setQTypeButtonHovered(true); }}
            onMouseLeave={!isOnboarding && section === 'apply' ? undefined : () => { setQTypeButtonHovered(false); setTimeout(() => { if (!qTypeMenuHovered && !qTypeButtonHovered) setShowQTypeMenu(false); }, 100); }}
            onClick={!isOnboarding && section === 'apply' ? () => { handleAddQuestion('reflection'); } : undefined}
            style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
          >
            + Add Question
          </button>
          {!(!isOnboarding && section === 'apply') && showQTypeMenu && (
            <div
              ref={dropdownRef}
              style={{ position: 'absolute', top: 44, left: 0, background: '#fff', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 2px 8px #0002', zIndex: 10 }}
              onMouseEnter={() => { setQTypeMenuHovered(true); setShowQTypeMenu(true); }}
              onMouseLeave={() => { setQTypeMenuHovered(false); setTimeout(() => { if (!qTypeMenuHovered && !qTypeButtonHovered) setShowQTypeMenu(false); }, 100); }}
            >
              {allowedTypes.includes('multi_choice') && (
                <div
                  style={{ padding: 10, cursor: 'pointer', background: hoveredOption === 'multi_choice' ? '#f3f4f6' : undefined }}
                  onMouseEnter={() => setHoveredOption('multi_choice')}
                  onMouseLeave={() => setHoveredOption(null)}
                  onClick={() => { handleAddQuestion('multi_choice'); setShowQTypeMenu(false); }}
                >Multi-Choice</div>
              )}
              {allowedTypes.includes('reflection') && (
                <div
                  style={{ padding: 10, cursor: 'pointer', background: hoveredOption === 'reflection' ? '#f3f4f6' : undefined }}
                  onMouseEnter={() => setHoveredOption('reflection')}
                  onMouseLeave={() => setHoveredOption(null)}
                  onClick={() => { handleAddQuestion('reflection'); setShowQTypeMenu(false); }}
                >Reflection</div>
              )}
              {allowedTypes.includes('information') && (
                <div
                  style={{ padding: 10, cursor: 'pointer', background: hoveredOption === 'information' ? '#f3f4f6' : undefined }}
                  onMouseEnter={() => setHoveredOption('information')}
                  onMouseLeave={() => setHoveredOption(null)}
                  onClick={() => { handleAddQuestion('information'); setShowQTypeMenu(false); }}
                >Information</div>
              )}
            </div>
          )}
        </div>
        )}
        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
        <button
          onClick={handleSaveLesson}
          disabled={saving || !isDirty()}
          style={{ width: '100%', padding: 12, background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, fontWeight: 600, cursor: saving || !isDirty() ? 'not-allowed' : 'pointer', marginTop: 16, opacity: saving || !isDirty() ? 0.5 : 1 }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      {/* Add Choice Pop-up */}
      {showAddCategory && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, minWidth: 300, boxShadow: '0 2px 16px #0003', position: 'relative' }}>
            <button onClick={() => { setShowAddCategory(false); setNewCategoryName(''); setCategoryError(''); }} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>&times;</button>
            <h4>Add Choice</h4>
            <input
              type="text"
              value={newCategoryName}
              onChange={e => { setNewCategoryName(e.target.value); setCategoryError(''); }}
              placeholder="Write Choice Name Here"
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', marginBottom: 12 }}
            />
            {categoryError && <div style={{ color: 'red', marginBottom: 8 }}>{categoryError}</div>}
            <button
              style={{ width: '100%', padding: 10, background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
              onClick={() => {
                const name = (newCategoryName || '').trim();
                if (!name) {
                  setCategoryError('Choice name is required');
                  return;
                }
                const existsInDb = categories.some(cat => normalizeName(cat.category_name) === normalizeName(name));
                const existsPending = pendingCategories.some(cat => normalizeName(cat.category_name) === normalizeName(name));
                if (existsInDb || existsPending) {
                  setCategoryError('Choice already exists');
                  return;
                }
                const newCat = { id: undefined, category_name: name, isPending: true };
                setPendingCategories(prev => [...prev, newCat]);
                // Auto-select for the invoking reflection question
                setQuestions(prev => {
                  if (activeCategoryQuestionIdx == null) return prev;
                  return prev.map((qq, i) => {
                    if (i !== activeCategoryQuestionIdx || qq.type !== 'reflection') return qq;
                    const already = (qq.categories || []).some(c => (c.id ? false : normalizeName(c.category_name) === normalizeName(name)) || (c.category_name && normalizeName(c.category_name) === normalizeName(name)));
                    if (already) return qq;
                    return { ...qq, categories: [...(qq.categories || []), newCat] };
                  });
                });
                setActiveCategoryQuestionIdx(null);
                setShowAddCategory(false);
                setNewCategoryName('');
                setCategoryError('');
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}
      <button onClick={onClose} style={{ marginBottom: 16, background: '#f3f4f6', color: '#222', border: '1px solid #bbb', borderRadius: 6, padding: '8px 18px', fontWeight: 500, fontSize: 15, cursor: 'pointer', position: 'absolute', left: 32, top: 12 }}>← Back to Course</button>
    </div>
  );
}

export default LessonModal; 